import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, type BrickowlPriceRefreshResult, type LotMarketplaceData, type LotMarketplacesData, type MarketFigures } from '../api';
import { ageDays } from '../lib/marketplaces';
import { Badge, Button, Card, ErrorMessage, Notice, Spinner, number, unitMoney, unitPlain, when } from '../ui';

const STALE_DAYS = 14;

/** A marketplace price, with a dagger and no symbol when the marketplace did not say which currency it means. */
function price(f: MarketFigures, v: string | null): string {
  return f.currency === null ? `${unitPlain(v)}†` : unitMoney(v, f.currency);
}

function Market({ f, headline, label }: { f: MarketFigures | null; headline: 'avg' | 'min'; label: string }) {
  if (f === null) return <span className="text-slate-300">—</span>;
  if (f.status === 'unavailable') return <span className="text-xs text-slate-400" title={f.note ?? undefined}>n/a</span>;
  const main = headline === 'avg' ? f.avgPrice : f.minPrice;
  if (main === null) return <span className="text-xs text-slate-500">none listed</span>;
  const stale = ageDays(f.fetchedAt) > STALE_DAYS;
  return (
    <span className={stale ? 'opacity-60' : undefined} title={`${label}, fetched ${when(f.fetchedAt)}${stale ? ' (out of date)' : ''}`}>
      <span className="font-medium tabular-nums">{price(f, main)}</span>
      <span className="block text-xs text-slate-500">
        {headline === 'avg' ? 'average' : 'lowest'}
        {f.lotCount !== null ? ` · ${number(f.lotCount)} lots` : ''}
      </span>
    </span>
  );
}

/** Your price against a marketplace's figure, as a percentage. Only when both are in the same, stated currency. */
function versus(own: string | null, ownCurrency: string, f: MarketFigures | null, headline: 'avg' | 'min'): string | null {
  const ref = f && f.status === 'ok' ? (headline === 'avg' ? f.avgPrice : f.minPrice) : null;
  if (own === null || ref === null || f?.currency !== ownCurrency) return null;
  const a = Number(own);
  const b = Number(ref);
  if (!(a > 0) || !(b > 0)) return null;
  const pct = Math.round(((a - b) / b) * 100);
  return pct === 0 ? 'level with it' : `${Math.abs(pct)}% ${pct > 0 ? 'above' : 'below'}`;
}

function Listing({ m }: { m: LotMarketplaceData }) {
  if (m.state === 'coming') return <span className="text-xs text-slate-400">Not connected yet</span>;
  // Listings read while the channel was on stay worth showing if it has since been switched off.
  if (m.state === 'off' && m.listings.length === 0) return <Badge>Not connected</Badge>;
  if (m.listings.length === 0) return <span className="text-xs text-slate-500">Not listed here</span>;
  return (
    <div className="space-y-1">
      {m.listings.map((x) => (
        <div key={x.remoteId} className="text-sm" title={`Last read ${when(x.lastSeenAt)}`}>
          <span className="tabular-nums">
            {number(x.qty)} @ {x.price ? unitMoney(x.price) : '—'}
          </span>{' '}
          {x.state === 'drift' && (
            <Badge tone="amber">differs from here</Badge>
          )}
          {x.status !== 'Y' && <span className="text-xs text-slate-500"> · {x.status === 'S' ? 'stockroom' : 'not for sale'}</span>}
          <div className="text-xs text-slate-500">Lot {x.remoteId}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Where this lot is listed on each marketplace, next to what each marketplace says the same colour and condition is
 * worth and how your price compares. It shows what has been fetched; Brick Owl prices are fetched on request.
 */
export function LotMarketplaces({ lotId, itemId, canRefresh }: { lotId: number; itemId: number; canRefresh: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lot-marketplaces', lotId], queryFn: () => api<LotMarketplacesData>(`/api/v1/lots/${lotId}/marketplaces`) });
  const refresh = useMutation({
    mutationFn: () => api<BrickowlPriceRefreshResult>(`/api/v1/items/${itemId}/marketplaces/brickowl/refresh`, { body: { force: false } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lot-marketplaces', lotId] });
      void qc.invalidateQueries({ queryKey: ['item-marketplaces', itemId] });
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <ErrorMessage error={q.error} />;
  const d = q.data;
  const bo = d.marketplaces.find((m) => m.key === 'brickowl');
  const r = refresh.data?.result;
  const unstated = d.marketplaces.some((m) => m.market.forSale?.status === 'ok' && m.market.forSale.avgPrice !== null && m.market.forSale.currency === null);
  return (
    <Card
      title="Marketplaces"
      className="lg:col-span-3"
      actions={
        bo?.state === 'ready' && canRefresh ? (
          <Button small variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Asking Brick Owl…' : 'Refresh Brick Owl prices'}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-3">
        <ErrorMessage error={refresh.error} />
        {r && (
          <Notice tone={r.stoppedBecause || r.unmapped.length > 0 ? 'amber' : 'green'}>
            Looked up {r.looked} {r.looked === 1 ? 'colour' : 'colours'} on Brick Owl.
            {r.unmapped.length > 0 && ` Could not look up ${r.unmapped.map((u) => `${u.colorName} (${u.reason})`).slice(0, 3).join('; ')}.`}
            {r.stoppedBecause && ` Stopped: ${r.stoppedBecause}`}
          </Notice>
        )}
        <div className="overflow-x-auto">
          <table className="data w-full min-w-[44rem] text-sm">
            <thead>
              <tr>
                <th>Marketplace</th>
                <th>This lot there</th>
                <th>For sale now</th>
                <th>Sold, last 6 months</th>
                <th>Your price {d.price ? `(${unitMoney(d.price, d.currency)})` : '(none)'}</th>
              </tr>
            </thead>
            <tbody>
              {d.marketplaces.map((m) => {
                const sale = versus(d.price, d.currency, m.market.forSale, m.key === 'bricklink' ? 'avg' : 'min');
                const sold = versus(d.price, d.currency, m.market.sold, 'avg');
                return (
                  <tr key={m.key}>
                    <td className="font-medium">{m.name}</td>
                    <td>
                      <Listing m={m} />
                    </td>
                    <td>
                      <Market f={m.market.forSale} headline={m.key === 'bricklink' ? 'avg' : 'min'} label="For sale now" />
                    </td>
                    <td>{m.key === 'bricklink' ? <Market f={m.market.sold} headline="avg" label="Sold" /> : <span className="text-slate-300">—</span>}</td>
                    <td className="text-xs text-slate-600">
                      {sale && <div>{sale} the for-sale price</div>}
                      {sold && <div>{sold} what it sold for</div>}
                      {!sale && !sold && <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500">
          This is what BrickLoop last read: opening a lot asks no marketplace anything.{' '}
          <Link to={`/items/${itemId}`} className="text-blue-700 hover:underline">
            All colours of this item
          </Link>
          .{unstated && ' † Brick Owl does not say which currency its prices are in, so no symbol is shown and no comparison is made.'}
        </p>
      </div>
    </Card>
  );
}
